import ApiService from './ApiService';

const PageService = {
  getPagesByProjectId: async function (projectId) {
    const endpoint = `/pages/project/${projectId}`;
    return await ApiService.get(endpoint);
  },
  getAllPagesByProjectId: async function (projectId) {
    const endpoint = `/pages/project/all/${projectId}`;
    return await ApiService.get(endpoint);
  },
  getAllPresetsByProjectId: async function (projectId) {
    const endpoint = `/pages/preset/all/${projectId}`;
    return await ApiService.get(endpoint);
  },
  getPageById: async function (pageId) {
    const endpoint = `/pages/${pageId}`;
    return await ApiService.get(endpoint);
  },
  createPage: async function (projectID, pageName, projectURL, parentID) {
    const endpoint = `/pages`;
    return await ApiService.post(endpoint, {
      label: pageName ? pageName : null,
      url: projectURL,
      project_id: projectID,
      parent_id: parentID,
    });
  },
  updatePage: async function (pageId, payload) {
    const endpoint = `/pages/${pageId}`;
    return await ApiService.put(endpoint, payload);
  },
  updatePages: async function (projectsData) {
    const endpoint = `/pages`;
    return await ApiService.put(endpoint, projectsData);
  },
  deletePages: async function (pageIds) {
    const endpoint = '/pages/delete';
    return await ApiService.post(endpoint, { ids: pageIds });
  },
  getAllTestCasesByPageId: async function (pageId) {
    const endpoint = `/test-case/page/${pageId}/?pageSize=1000`;
    return await ApiService.get(endpoint);
  },
  discoverTestCasesByPageId: async function (pageId, context = {}) {
    const endpoint = `/test-case/discover/${pageId}`;
    return await ApiService.post(endpoint, context);
  },
  discoverTestCasesMultiple: async function (pageIds) {
    const endpoint = '/test-case/discover-multiple';
    return await ApiService.post(endpoint, { pageIds });
  },
  regenerateTestCasesById: async function (id, context = {}) {
    const endpoint = `/test-case/regenerate/${id}`;
    return await ApiService.post(endpoint, {
      requirements: context.requirements || '',
      expectedTestCases: context.expectedTestCases || '',
    });
  },
  regenerateTestCasesMultipleByIds: async function (testCaseIds, context = {}) {
    const endpoint = '/test-case/regenerate-multiple';
    return await ApiService.post(endpoint, { 
      testCaseIds,
      requirements: context.requirements || '',
      expectedTestCases: context.expectedTestCases || '',
    });
  },
  getTestCaseById: async function (testCaseId) {
    const endpoint = `/test-case/${testCaseId}`;
    return await ApiService.get(endpoint);
  },
  updateTestCaseById: async function (testCaseId, data) {
    const endpoint = `/test-case/update/${testCaseId}`;
    return await ApiService.post(endpoint, { data });
  },
  updateTestCaseFeedback: async function (testCaseId, value) {
    const endpoint = `/test-case/feedback/${testCaseId}`;
    return await ApiService.put(endpoint, { value });
  },
  updateTestCaseStepValue: async function (testCaseStepId, newValue) {
    const endpoint = `/test-case/update-step/${testCaseStepId}`;
    return await ApiService.post(endpoint, { value: newValue });
  },
  uploadTestCaseStepFile: async function (testCaseStepId, file) {
    const endpoint = `/test-case/upload-step-file/${testCaseStepId}`;
    const formData = new FormData();
    formData.append('file', file);
    return await ApiService.post(endpoint, formData);
  },
  deleteTestCases: async function (testCaseIds) {
    const endpoint = '/test-case/delete';
    return await ApiService.post(endpoint, { ids: testCaseIds });
  },
  deleteTestRuns: async function (testRunIds) {
    const endpoint = '/test-case/delete-runs';
    return await ApiService.post(endpoint, { ids: testRunIds });
  },
  getAllTestRunsByTestCaseId: async function (testCaseId) {
    const endpoint = `/test-case/runs/${testCaseId}/?pageSize=1000`;
    return await ApiService.get(endpoint);
  },
  runTestCase: async function (testCaseId, options = {}) {
    const endpoint = '/test-case/run';
    return await ApiService.post(endpoint, { ids: [testCaseId], ...options });
  },
  runTestCases: async function (testCaseIds, options = {}) {
    const endpoint = '/test-case/run';
    return await ApiService.post(endpoint, { ids: testCaseIds, ...options });
  },
  getTestRunById: async function (testRunId) {
    const endpoint = `/test-case/run/${testRunId}`;
    return await ApiService.get(endpoint);
  },
  deleteTestRunById: async function (testRunId) {
    const endpoint = `/test-case/run/${testRunId}`;
    return await ApiService.delete(endpoint);
  },
  createPreset: async function (name, projectId, pagesIds) {
    const endpoint = '/pages/preset';
    return await ApiService.post(endpoint, {
      name,
      project_id: projectId,
      page_ids: pagesIds,
    });
  },
  updatePreset: async function (presetId, newName) {
    const endpoint = `/pages/preset/${presetId}`;
    return await ApiService.put(endpoint, { name: newName });
  },
  deletePreset: async function (presetId) {
    const endpoint = `/pages/preset/${presetId}`;
    return await ApiService.delete(endpoint);
  },
  generateNegativeScenarios: async function (testCaseId) {
    const res = await ApiService.post(
        `/test-case/discover-negatives/${testCaseId}`
    );
    return res.data;
  },
  getTestCaseHierarchy: async function (projectId) {
    const endpoint = `/test-case/hierarchy/${projectId}`;
    return await ApiService.get(endpoint);
  },
  createProjectVersion: async function (projectId, name) {
    const endpoint = `/test-case/project-version`;
    return await ApiService.post(endpoint, { projectId, name });
  },
  updateProjectVersion: async function (versionId, name) {
    const endpoint = `/test-case/project-version/${versionId}`;
    return await ApiService.patch(endpoint, { name });
  },
  deleteProjectVersion: async function (versionId) {
    const endpoint = `/test-case/project-version/${versionId}`;
    return await ApiService.delete(endpoint);
  },
  createTestCaseCycle: async function ({
    projectVersionId,
    name,
    description,
    versionTag,
    dateFrom,
    dateTo,
  }) {
    const endpoint = `/test-case/cycle`;
    return await ApiService.post(endpoint, {
      projectVersionId,
      name,
      description,
      versionTag,
      dateFrom,
      dateTo,
    });
  },
  updateTestCaseCycle: async function (
    cycleId,
    { projectVersionId, name, description, versionTag, dateFrom, dateTo }
  ) {
    const endpoint = `/test-case/cycle/${cycleId}`;
    return await ApiService.patch(endpoint, {
      projectVersionId,
      name,
      description,
      versionTag,
      dateFrom,
      dateTo,
    });
  },
  deleteTestCaseCycle: async function (cycleId) {
    const endpoint = `/test-case/cycle/${cycleId}`;
    return await ApiService.delete(endpoint);
  },
  addTestsToCycle: async function (cycleId, { testCaseIds = [], pageIds = [] }) {
    const endpoint = `/test-case/cycle/${cycleId}/items`;
    return await ApiService.post(endpoint, { testCaseIds, pageIds });
  },
  removeCycleItem: async function (cycleItemId) {
    const endpoint = `/test-case/cycle-item/${cycleItemId}`;
    return await ApiService.delete(endpoint);
  },
  removeCyclePage: async function (cycleId, pageId) {
    const endpoint = `/test-case/cycle/${cycleId}/page/${pageId}`;
    return await ApiService.delete(endpoint);
  },
  getCyclePageTestCases: async function (cycleId, pageId) {
    const endpoint = `/test-case/cycle/${cycleId}/page/${pageId}`;
    return await ApiService.get(endpoint);
  },
};

export default PageService;
